import { Box, Container } from '@mui/material';
import * as React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import * as forms from '../../components/forms/OneLinkSummary';
import { AlertErrorBanner } from '../../components/_mui/_components_';
import { ConfirmationDialog, ErrorInformationModal } from '../../components/_mui/_dialogs_';
import { CUSTOM } from '../../constants/campaigns';
import { useCampaignSummaries } from '../../service/InventoryService';
import { selectAddressBook } from '../../store/selectors/addressBook';
import { selectIsOneLinkEditing } from '../../store/selectors/oneLink';
import { selectBusinessReasons } from '../../store/selectors/reasons';
import { selectUsers } from '../../store/selectors/users';
import { IOneLinkWithCampaignSummaries, OneLinkBannerEnum, OneLinkModalEnum } from '../../types/oneLink';
import { IApiError } from '../../types/shell';
import { createOneLinkValidationSchema, mapInstanceData, mapOLValidationErrors } from '../../utils/oneLink';
import { IOneLinkErrors, OneLinkChunksEnum, TUpdateOneLinkChunk } from './types';

export interface IOneLinkChunkFormProps {
  instance: IOneLinkWithCampaignSummaries | null;
  onClose: () => void;
  onSubmit: (chunk: TUpdateOneLinkChunk) => void;
  isLoading?: boolean;
}

type TFormElement = <T extends IOneLinkChunkFormProps>(p: T) => React.ReactElement<T> | null;

interface IOneLinkSummaryContext {
  onDeactivate?: () => void;
  onActivate?: () => void;
  onSubmit: (chunk: TUpdateOneLinkChunk) => void;
  handleEdit: (mode: OneLinkChunksEnum | null) => void;
  handleValidation: () => void;
  instance: IOneLinkWithCampaignSummaries | null;
  modes: typeof OneLinkChunksEnum;
  active: OneLinkChunksEnum | null;
  title: string;
  Form: TFormElement;
  isLoading: boolean;
  errors?: IOneLinkErrors;
  modalType: OneLinkModalEnum;
  handleCloseModal: () => void;
  handleCloseBanner: () => void;
}

const OneLinkContext = createContext<IOneLinkSummaryContext>({
  onSubmit: (_: TUpdateOneLinkChunk) => void 0,
  handleEdit: (_: OneLinkChunksEnum | null) => void 0,
  onDeactivate: () => void 0,
  onActivate: () => void 0,
  handleValidation: () => void 0,
  instance: null,
  modes: OneLinkChunksEnum,
  active: null,
  title: '',
  Form: () => null,
  isLoading: false,
  errors: undefined,
  modalType: OneLinkModalEnum.None,
  handleCloseModal: () => void 0,
  handleCloseBanner: () => void 0,
});

interface IProps {
  children: React.ReactNode;
  onUpdate: (chunk: TUpdateOneLinkChunk) => Promise<IApiError | void>;
  onDeactivate?: (instance: IOneLinkWithCampaignSummaries) => Promise<void>;
  onActivate?: (instance: IOneLinkWithCampaignSummaries) => Promise<void | IApiError>;
  instance: IOneLinkWithCampaignSummaries | null;
}

export const OneLinkSummaryProvider = ({ children, onUpdate, onDeactivate, onActivate, instance }: IProps) => {
  const [sidebarMode, setSidebarMode] = useState<OneLinkChunksEnum | null>(null);
  const [errors, setErrors] = useState<IOneLinkErrors | undefined>();
  const [modalType, setModalType] = useState<OneLinkModalEnum>(OneLinkModalEnum.None);
  const [bannerType, setBannerType] = useState<OneLinkBannerEnum>(OneLinkBannerEnum.None);

  const isLoading = useSelector(selectIsOneLinkEditing);
  const users = useSelector(selectUsers);
  const reasons = useSelector(selectBusinessReasons);
  const addressBook = useSelector(selectAddressBook);

  const handleUpdate = useCallback(
    (chunk: TUpdateOneLinkChunk) => {
      onUpdate(chunk).then(() => {
        setSidebarMode(null);
      });
    },
    [onUpdate],
  );

  const handleCloseModal = useCallback(() => {
    setModalType(OneLinkModalEnum.None);
  }, []);

  const handleCloseBanner = useCallback(() => {
    setBannerType(OneLinkBannerEnum.None);
  }, []);

  const {
    title,
    Form,
  }: {
    title: string;
    Form: TFormElement;
  } = React.useMemo(() => {
    switch (sidebarMode) {
      case OneLinkChunksEnum.SummaryInfo:
        return { title: 'Edit Summary', Form: forms.SummaryForm };
      case OneLinkChunksEnum.InventoryInfo:
        return { title: 'Edit Inventory', Form: forms.InventoryForm };
      case OneLinkChunksEnum.ShippingInfo:
        return { title: 'Edit Shipping', Form: forms.ShippingForm };
      case OneLinkChunksEnum.AdditionalInfo:
        return { title: 'Edit Additional Details', Form: forms.AdditionalForm };
      case OneLinkChunksEnum.SecurityInfo:
        return { title: 'Edit Security', Form: forms.SecurityForm };
      case OneLinkChunksEnum.TwoFactorInfo:
        return { title: 'Edit 2FA', Form: forms.TwoFactorForm };
      default: {
        console.warn('Unreachable scenario. Unknown form type');
        return { title: '', Form: () => null };
      }
    }
  }, [sidebarMode]);

  const handleDeactivate = useCallback(() => {
    if (onDeactivate && instance) {
      onDeactivate(instance as IOneLinkWithCampaignSummaries);
    }
  }, [onDeactivate, instance]);

  const { data: singleCampaignSummary } = useCampaignSummaries(
    { campaign_ids: instance?.pre_created_engagement_id ? [instance.pre_created_engagement_id] : undefined },
    instance?.pre_created_engagement_id !== CUSTOM,
  );

  const isBusinessReasonRequiredForSingleCampaign = useMemo(
    () => singleCampaignSummary && singleCampaignSummary[0].business_reason_required,
    [singleCampaignSummary],
  );

  const validationSchema = useMemo(
    () =>
      createOneLinkValidationSchema({
        users,
        reasons,
        instance,
        addressBook,
        isBusinessReasonRequiredForSingleCampaign,
      }),
    [users, reasons, instance, addressBook, isBusinessReasonRequiredForSingleCampaign],
  );

  const mappedInstance = React.useMemo(() => {
    return mapInstanceData(instance);
  }, [instance]);

  const handleValidation = useCallback(() => {
    validationSchema
      .validate(mappedInstance, { abortEarly: false })
      .then(() => {
        setModalType(OneLinkModalEnum.Confirmation);
        setBannerType(OneLinkBannerEnum.None);
      })
      .catch((err) => {
        const errorResult = mapOLValidationErrors(err);
        setErrors(errorResult);
        setModalType(OneLinkModalEnum.Error);
        setBannerType(OneLinkBannerEnum.Error);
      });
  }, [validationSchema, mappedInstance]);

  const handleEdit = useCallback((mode: OneLinkChunksEnum | null) => {
    setSidebarMode(mode);
  }, []);

  const handleActivate = useCallback(async () => {
    setModalType(OneLinkModalEnum.None);
    setErrors(undefined);
    await onActivate?.(instance as IOneLinkWithCampaignSummaries).catch((apiError) => {
      if ('errors' in apiError) {
        setErrors(apiError.errors);
        setModalType(OneLinkModalEnum.Error);
        setBannerType(OneLinkBannerEnum.Error);
      }
    });
  }, [onActivate, instance]);

  const value = useMemo(
    () => ({
      instance,
      handleEdit,
      onSubmit: handleUpdate,
      onDeactivate: handleDeactivate,
      handleValidation,
      modes: OneLinkChunksEnum,
      title,
      Form,
      active: sidebarMode,
      isLoading,
      errors,
      modalType,
      handleCloseModal,
      handleCloseBanner,
    }),
    [
      handleValidation,
      handleDeactivate,
      isLoading,
      instance,
      handleEdit,
      handleUpdate,
      title,
      Form,
      sidebarMode,
      errors,
      modalType,
      handleCloseModal,
      handleCloseBanner,
    ],
  );

  return (
    <OneLinkContext.Provider value={value}>
      <ConfirmationDialog
        open={modalType === OneLinkModalEnum.Confirmation}
        title="Confirm One Link Activation"
        message="Your One Link is ready to activate. You can deactivate it anytime if needed. Click 'Activate' to continue."
        onClose={handleCloseModal}
        onConfirm={handleActivate}
        confirmButtonText="Activate"
        isLoading={isLoading}
      />
      <ErrorInformationModal
        trigger={modalType === OneLinkModalEnum.Error}
        onClose={handleCloseModal}
        errors={errors}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        {bannerType === OneLinkBannerEnum.Error && (
          <Container
            maxWidth="xl"
            sx={{
              paddingLeft: { lg: 12 },
              marginTop: 2,
              marginBottom: -3,
              '@media (min-width: 1200px) and (max-width: 1367px)': {
                paddingLeft: 3,
              },
            }}
          >
            <AlertErrorBanner onClose={handleCloseBanner} errors={errors} />
          </Container>
        )}
        {children}
      </Box>
    </OneLinkContext.Provider>
  );
};

export const useOneLinkSummary = () => {
  return useContext(OneLinkContext);
};
